/**
 * NotificationErrorBoundary - catches unexpected render errors inside the
 * notification-bell plugin so they don't bubble up and crash the host app.
 */
import { Component, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
}

export class NotificationErrorBoundary extends Component<Props, State> {
  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => this.setState({ error: null })}
              >
                Retry
              </Button>
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Something went wrong
            </Typography>
            <Typography variant="body2">{error.message}</Typography>
          </Alert>
        </Box>
      );
    }
    return this.props.children;
  }
}
